/**
 * sl-9qm.14.3: Custom Action Types
 *
 * Defines row-level actions: status change, archive, automation trigger.
 * Stored in ViewConfig.settings.inlineEdit.customActions[].
 */
import { z } from 'zod';

/** Action type identifier */
export type CustomActionType = 'status-change' | 'archive' | 'automation';

/** Base action configuration shared by all types */
interface CustomActionBase {
  /** Unique action ID */
  id: string;
  /** Display label */
  label: string;
  /** Optional icon name */
  icon?: string;
  /** Action type discriminator */
  type: CustomActionType;
}

/** Status-change action: updates a select column value */
export interface StatusChangeAction extends CustomActionBase {
  type: 'status-change';
  /** Target column ID (must be a select column) */
  columnId: string;
  /** Value to set */
  targetValue: string;
}

/** Archive action: sets a boolean column to true */
export interface ArchiveAction extends CustomActionBase {
  type: 'archive';
  /** Target column ID (must be a boolean/checkbox column) */
  columnId: string;
}

/** Automation action: fires an event to StreamLainEventBus */
export interface AutomationAction extends CustomActionBase {
  type: 'automation';
  /** Automation block ID to trigger */
  automationBlockId: string;
}

/** Union of all custom actions */
export type CustomAction =
  | StatusChangeAction
  | ArchiveAction
  | AutomationAction;

// ─── Zod Schemas ────────────────────────────────────────────────────

const statusChangeActionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  icon: z.string().optional(),
  type: z.literal('status-change'),
  columnId: z.string().min(1),
  targetValue: z.string().min(1),
});

const archiveActionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  icon: z.string().optional(),
  type: z.literal('archive'),
  columnId: z.string().min(1),
});

const automationActionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  icon: z.string().optional(),
  type: z.literal('automation'),
  automationBlockId: z.string().min(1),
});

export const customActionSchema = z.discriminatedUnion('type', [
  statusChangeActionSchema,
  archiveActionSchema,
  automationActionSchema,
]);

export const customActionsArraySchema = z.array(customActionSchema).default([]);
